import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { isSameDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { NewFeedbackForm } from '@/components/features/feedback/forms/new-feedback-form';
import { useCurrentOrganization } from '@/components/features/membership/membership-provider';
import { SchedulePastille } from '@/components/features/schedule/schedule-pastille';
import { Card, CardContent } from '@/components/ui/card';
import {
  Page,
  PageContent,
  PageHeader,
  PageHeaderDescription,
  PageHeaderInfo,
  PageHeaderTitle,
} from '@/components/ui/layout/page';
import { useFormatDate } from '@/hooks/use-format-date';
import { capitalize } from '@/lib/utils';
import { createScheduleFeedbackMutation } from '@/mutations/feedback/createScheduleFeedbackMutation';
import { getScheduleQuery } from '@/queries/getScheduleQuery';
import { getScheduleRotationByIdQuery } from '@/queries/getScheduleRotationByIdQuery';

export const Route = createFileRoute('/_authenticated/me/feedback')({
  component: RouteComponent,
  validateSearch: z.object({
    rotationId: z.string().uuid(),
    scheduleId: z.string().uuid(),
  }),
  beforeLoad: async ({ search, context }) => {
    const { scheduleId, rotationId } = search;

    const [schedule, rotation] = await Promise.allSettled([
      context.queryClient.ensureQueryData(
        getScheduleQuery(context.currentOrganizationId, scheduleId)
      ),
      context.queryClient.ensureQueryData(
        getScheduleRotationByIdQuery({
          organizationId: context.currentOrganizationId,
          scheduleId,
          rotationId,
        })
      ),
    ]);

    if (schedule.status === 'rejected' || rotation.status === 'rejected') {
      throw redirect({
        to: '/me',
      });
    }
  },
});

function RouteComponent() {
  const { rotationId, scheduleId } = Route.useSearch();
  const { t } = useTranslation();
  const organization = useCurrentOrganization();
  const navigate = useNavigate();
  const { formatWithPreset } = useFormatDate();

  const { data: schedule } = useSuspenseQuery({
    ...getScheduleQuery(organization.organizationId, scheduleId),
  });

  const { data: rotation } = useSuspenseQuery({
    ...getScheduleRotationByIdQuery({
      organizationId: organization.organizationId,
      scheduleId,
      rotationId,
    }),
  });

  const { mutate: createScheduleFeedback } = useMutation({
    ...createScheduleFeedbackMutation({
      organizationId: organization.organizationId,
      scheduleId,
    }),
    onSuccess: () => {
      toast.success(t('authenticated.me.feedback.toast.success'));
      navigate({
        to: '/me',
      });
    },
    onError: () => {
      toast.error(t('authenticated.me.feedback.toast.error'));
    },
  });

  return (
    <Page>
      <PageHeader>
        <PageHeaderInfo>
          <div className="flex items-center gap-2">
            <SchedulePastille
              color={schedule?.color}
              size="lg"
              variant="line"
            />
            <PageHeaderTitle>
              {capitalize(schedule?.name ?? '')}
            </PageHeaderTitle>
          </div>
          {isSameDay(rotation?.startDate, rotation?.endDate) ? (
            <PageHeaderDescription>
              {t('authenticated.me.feedback.description_sameDay', {
                date: formatWithPreset(rotation?.startDate, 'month_day_year'),
              })}
            </PageHeaderDescription>
          ) : (
            <PageHeaderDescription>
              {t('authenticated.me.feedback.description', {
                rotationId,
                startDate: formatWithPreset(
                  rotation?.startDate,
                  'month_day_year'
                ),
                endDate: formatWithPreset(rotation?.endDate, 'month_day_year'),
              })}
            </PageHeaderDescription>
          )}
        </PageHeaderInfo>
      </PageHeader>

      <PageContent>
        <Card>
          <CardContent>
            <NewFeedbackForm
              onSubmit={(values) => {
                createScheduleFeedback({
                  rotationId,
                  score: values.rating,
                  text: values.comment,
                });
              }}
            />
          </CardContent>
        </Card>
      </PageContent>
    </Page>
  );
}
